import React from 'react';
import {Box, Checkbox, IconButton, Popover, Slider, Stack, Typography} from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import {SplitOptionsType, useSplitContext} from 'providers/SplitProvider';

const SPEED_OPTIONS = [1, 3, 5, 7, 10, 20, 50, 100] as const;

const speedToSlider = (speed: number) => {
  const index = SPEED_OPTIONS.indexOf(speed as (typeof SPEED_OPTIONS)[number]);
  return index === -1 ? SPEED_OPTIONS.indexOf(10) : index;
};

const sliderToSpeed = (value: number) => SPEED_OPTIONS[value] ?? 10;

const SelectPreference = ({ compact = false }: SelectPreferenceProps) => {
  const {options, setOptions} = useSplitContext();
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const onChangeOption =
    (key: keyof Pick<SplitOptionsType, 'speed' | 'opacity' | 'weight'>) => (_: Event, value: number | number[]) => {
      const next = Array.isArray(value) ? value[0] : value;
      setOptions({
        ...options,
        [key]: key === 'speed' ? sliderToSpeed(next) : next
      });
    };

  const onChangeTimeGradient = () => {
    setOptions({...options, timeGradient: !options.timeGradient});
  };

  const onChangeAutoZoom = () => {
    setOptions({...options, autoZoom: !options.autoZoom});
  };

  return (
    <>
      <IconButton
        aria-label="Display preferences"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        size="small"
        sx={{ minWidth: compact ? 36 : 44, minHeight: compact ? 36 : 44, p: compact ? 0.5 : 1 }}
      >
        <TuneIcon fontSize={compact ? 'small' : 'medium'} />
      </IconButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{vertical: 'top', horizontal: 'right'}}
        transformOrigin={{vertical: 'bottom', horizontal: 'right'}}
      >
        <Box sx={{p: 2, width: 260}}>
          <Stack spacing={2}>
            <Box>
              <Typography variant="body2" gutterBottom>
                Speed: {options.speed}x
              </Typography>
              <Slider
                size="small"
                min={0}
                max={SPEED_OPTIONS.length - 1}
                step={1}
                value={speedToSlider(options.speed)}
                onChange={onChangeOption('speed')}
                valueLabelDisplay="off"
              />
            </Box>
            <Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <Checkbox checked={options.timeGradient} onChange={onChangeTimeGradient} size="small" sx={{padding: 0}} />
                <Typography variant="body2">Time gradient</Typography>
              </Stack>
            </Box>
            <Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <Checkbox checked={options.autoZoom} onChange={onChangeAutoZoom} size="small" sx={{padding: 0}} />
                <Typography variant="body2">Auto zoom</Typography>
              </Stack>
            </Box>
            <Box>
              <Typography variant="body2" gutterBottom>
                Opacity: {options.opacity}%
              </Typography>
              <Slider
                size="small"
                min={0}
                max={100}
                step={1}
                value={options.opacity}
                onChange={onChangeOption('opacity')}
                valueLabelDisplay="off"
              />
            </Box>
            <Box>
              <Typography variant="body2" gutterBottom>
                Weight: {options.weight}
              </Typography>
              <Slider
                size="small"
                min={1}
                max={12}
                step={1}
                value={options.weight}
                onChange={onChangeOption('weight')}
                valueLabelDisplay="off"
              />
            </Box>
          </Stack>
        </Box>
      </Popover>
    </>
  );
};

export default SelectPreference;

type SelectPreferenceProps = {
  compact?: boolean;
};
